import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, Layers, Plus, ArrowUp, ArrowDown, Pencil, ExternalLink, Eye, EyeOff, Lock, MapPin,
} from "lucide-react";

const NAVY = "#1C4358";

const ScopeBadge = ({ kind }) => {
  const map = {
    GLOBAL: { bg: "rgba(28,67,88,0.10)", c: NAVY },
    LOCAL: { bg: "rgba(47,125,90,0.12)", c: "#2F7D5A" },
    HYBRID: { bg: "rgba(200,140,20,0.14)", c: "#B7791F" },
  };
  const s = map[kind] || map.GLOBAL;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: s.bg, color: s.c }}>{kind}</span>;
};

export default function ServiceLanes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newLane, setNewLane] = useState({ name: "", slug: "" });
  const isSuper = user?.role === "super_admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site/service-lanes");
      setLanes(data.items || []);
    } catch { toast.error("Could not load service lanes."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const move = async (idx, dir) => {
    const next = [...lanes];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setLanes(next);
    setSavingOrder(true);
    try {
      await api.put("/site/service-lanes/reorder", { order: next.map((l, i) => ({ id: l.id, sort_order: i + 1 })) });
    } catch { toast.error("Reorder failed."); load(); }
    finally { setSavingOrder(false); }
  };

  const toggleActive = async (lane) => {
    try {
      await api.put(`/site/service-lanes/${lane.id}`, { active: !lane.active });
      setLanes((ls) => ls.map((l) => (l.id === lane.id ? { ...l, active: !l.active } : l)));
      toast.success(`${lane.name} ${!lane.active ? "activated" : "deactivated"}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  const createLane = async () => {
    if (!newLane.name.trim()) { toast.error("Name is required."); return; }
    try {
      const { data } = await api.post("/site/service-lanes", newLane);
      toast.success("Service lane created (draft).");
      setCreateOpen(false);
      setNewLane({ name: "", slug: "" });
      navigate(`/site/service-lanes/${data.lane.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="service-lanes-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Layers size={22} style={{ color: NAVY }} />
            <h1 className="text-2xl font-display font-bold text-[#1C4358]">Service Lane CMS</h1>
            <ScopeBadge kind="GLOBAL" />
          </div>
          <p className="text-[14px] text-[#4B5563] mt-1">Global service lane content powering the public /services pages. Local managers can add scoped overrides.</p>
        </div>
        {isSuper && (
          <Button data-testid="create-lane-btn" onClick={() => setCreateOpen(true)} className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
            <Plus size={16} /> New service lane
          </Button>
        )}
      </div>

      {!isSuper && (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-[rgba(28,67,88,0.05)] px-4 py-3 text-[14px] text-[#1C4358]">
          <Lock size={15} /> Global content is read-only for your role. Manage your local overrides in <button className="underline font-semibold" onClick={() => navigate("/site/local")}>Local Overrides</button>.
        </div>
      )}

      <div className="mt-6 space-y-3">
        {lanes.map((l, i) => (
          <Card key={l.id} data-testid={`lane-row-${l.slug}`} className="p-4 border-[#E6EDF2] flex items-center gap-4">
            <div className="flex flex-col gap-1">
              <button disabled={!isSuper || i === 0 || savingOrder} onClick={() => move(i, -1)} data-testid={`lane-up-${l.slug}`}
                className="text-[#1C4358] disabled:opacity-25 hover:text-[#9C1E22]"><ArrowUp size={16} /></button>
              <button disabled={!isSuper || i === lanes.length - 1 || savingOrder} onClick={() => move(i, 1)} data-testid={`lane-down-${l.slug}`}
                className="text-[#1C4358] disabled:opacity-25 hover:text-[#9C1E22]"><ArrowDown size={16} /></button>
            </div>
            <img src={l.hero_image_url} alt="" className="h-14 w-20 rounded-lg object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-semibold text-[#1C4358] text-[16px] truncate">{l.name}</span>
                {!l.active && <Badge variant="outline" className="text-[10px] border-[#9C1E22]/40 text-[#9C1E22]">Inactive</Badge>}
                <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: l.approval_status === "approved" ? "rgba(47,125,90,0.12)" : "rgba(200,140,20,0.14)", color: l.approval_status === "approved" ? "#2F7D5A" : "#B7791F" }}>{l.approval_status}</span>
              </div>
              <div className="text-[13px] text-[#4B5563] mt-0.5">/services/{l.slug}</div>
              {l.local_override_franchises?.length > 0 && (
                <div className="text-[12px] text-[#2F7D5A] mt-1 flex items-center gap-1"><MapPin size={12} /> Local overrides: {l.local_override_franchises.join(", ")}</div>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <a href={`/services/${l.slug}`} target="_blank" rel="noreferrer" title="Preview" data-testid={`lane-preview-${l.slug}`}
                className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-[#1C4358] hover:bg-[#1C4358]/8"><ExternalLink size={16} /></a>
              {isSuper && (
                <button onClick={() => toggleActive(l)} title={l.active ? "Deactivate" : "Activate"} data-testid={`lane-toggle-${l.slug}`}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-[#1C4358] hover:bg-[#1C4358]/8">{l.active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
              )}
              <Button variant="outline" size="sm" data-testid={`lane-edit-${l.slug}`} onClick={() => navigate(`/site/service-lanes/${l.id}`)} className="gap-1 border-[#D6E0E7] text-[#1C4358]">
                <Pencil size={14} /> {isSuper ? "Edit" : "View"}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-lane-dialog">
          <DialogHeader><DialogTitle>New service lane</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="new-lane-name" value={newLane.name} onChange={(e) => setNewLane((n) => ({ ...n, name: e.target.value }))} placeholder="e.g. Veteran Family Support" />
            </div>
            <div className="space-y-1.5">
              <Label>Slug (optional)</Label>
              <Input data-testid="new-lane-slug" value={newLane.slug} onChange={(e) => setNewLane((n) => ({ ...n, slug: e.target.value }))} placeholder="auto-generated from name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid="new-lane-submit" onClick={createLane} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white">Create draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
